import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import { z } from 'zod';
import type { CreateTenant, UpdateTenant, TenantWithStatus } from '../types';
import { CreateTenantSchema } from '@torviaai/types';

// Available AWS regions for tenant deployment
const AWS_REGIONS = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'Europe (Ireland)' },
    { value: 'eu-west-2', label: 'Europe (London)' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
];

export interface TenantFormProps {
    open: boolean;
    tenant?: TenantWithStatus; // undefined for create, populated for edit
    onSubmit: (data: CreateTenant | UpdateTenant) => Promise<void>;
    onCancel: () => void;
    loading: boolean;
    onValidateSubdomain?: (subdomain: string) => Promise<{ available: boolean; message?: string }>;
}

interface FormData {
    name: string;
    subDomain: string;
    region: string;
    initialUser: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

interface FormErrors {
    name?: string;
    subDomain?: string;
    region?: string;
    initialUser?: {
        firstName?: string;
        lastName?: string;
        email?: string;
    };
    general?: string;
}

export function TenantForm({ open, tenant, onSubmit, onCancel, loading, onValidateSubdomain }: TenantFormProps) {
    const isEditMode = Boolean(tenant);

    const [formData, setFormData] = useState<FormData>({
        name: '',
        subDomain: '',
        region: '',
        initialUser: {
            firstName: '',
            lastName: '',
            email: '',
        },
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [subdomainValidating, setSubdomainValidating] = useState(false);
    const [subdomainValidationMessage, setSubdomainValidationMessage] = useState<string>('');

    // Reset form when dialog opens/closes or tenant changes
    useEffect(() => {
        if (open) {
            if (tenant) {
                // Edit mode - populate with existing data
                setFormData({
                    name: tenant.name,
                    subDomain: tenant.subDomain,
                    region: tenant.region,
                    initialUser: {
                        firstName: tenant.initialUser?.firstName || '',
                        lastName: tenant.initialUser?.lastName || '',
                        email: tenant.initialUser?.email || '',
                    },
                });
            } else {
                // Create mode - reset to empty
                setFormData({
                    name: '',
                    subDomain: '',
                    region: AWS_REGIONS[0].value, // Default to first region
                    initialUser: {
                        firstName: '',
                        lastName: '',
                        email: '',
                    },
                });
            }
            setErrors({});
            setSubdomainValidationMessage('');
        }
    }, [open, tenant]);

    // Debounced subdomain validation
    const validateSubdomainDebounced = useCallback(
        debounce(async (subdomain: string) => {
            if (!subdomain || !onValidateSubdomain || isEditMode) return;

            setSubdomainValidating(true);
            setSubdomainValidationMessage('');

            try {
                const result = await onValidateSubdomain(subdomain);
                if (result.available) {
                    setSubdomainValidationMessage('✓ Subdomain is available');
                    setErrors((prev) => ({ ...prev, subDomain: undefined }));
                } else {
                    const message = result.message || 'Subdomain is not available';
                    setSubdomainValidationMessage(message);
                    setErrors((prev) => ({ ...prev, subDomain: message }));
                }
            } catch (error) {
                const message = 'Failed to validate subdomain';
                setSubdomainValidationMessage(message);
                setErrors((prev) => ({ ...prev, subDomain: message }));
            } finally {
                setSubdomainValidating(false);
            }
        }, 500),
        [onValidateSubdomain, isEditMode]
    );

    // Handle form field changes
    const handleFieldChange = (field: keyof FormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));

        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }

        // Trigger subdomain validation
        if (field === 'subDomain' && value.trim()) {
            validateSubdomainDebounced(value.trim().toLowerCase());
        }
    };

    const handleInitialUserChange = (field: keyof FormData['initialUser'], value: string) => {
        setFormData((prev) => ({
            ...prev,
            initialUser: { ...prev.initialUser, [field]: value },
        }));

        // Clear field error when user starts typing
        if (errors.initialUser?.[field]) {
            setErrors((prev) => ({
                ...prev,
                initialUser: { ...prev.initialUser, [field]: undefined },
            }));
        }
    };

    // Validate form data
    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        try {
            if (isEditMode) {
                // For edit mode, only validate the name field (UpdateTenant only allows name updates)
                if (!formData.name || !formData.name.trim()) {
                    newErrors.name = 'Tenant name is required';
                }
            } else {
                // For create mode, validate the full CreateTenant schema
                CreateTenantSchema.parse(formData);
            }
        } catch (error) {
            if (error instanceof z.ZodError) {
                error.issues.forEach((err) => {
                    const path = err.path.join('.');
                    if (path.startsWith('initialUser.')) {
                        const field = path.replace('initialUser.', '') as keyof FormData['initialUser'];
                        if (!newErrors.initialUser) newErrors.initialUser = {};
                        // Only set error if not already set (first error wins)
                        if (!newErrors.initialUser[field]) {
                            newErrors.initialUser[field] = err.message;
                        }
                    } else {
                        const field = path as keyof Omit<FormErrors, 'initialUser' | 'general'>;
                        // Only set error if not already set (first error wins)
                        if (!newErrors[field]) {
                            newErrors[field] = err.message;
                        }
                    }
                });
            }
        }

        // Additional validation for subdomain availability in create mode
        if (!isEditMode && errors.subDomain) {
            newErrors.subDomain = errors.subDomain;
        }

        setErrors(newErrors);

        // Check if there are any errors (including nested ones)
        const hasErrors = Object.keys(newErrors).some((key) => {
            if (key === 'initialUser') {
                return newErrors.initialUser && Object.keys(newErrors.initialUser).length > 0;
            }
            return Boolean(newErrors[key as keyof FormErrors]);
        });

        return !hasErrors;
    };

    // Handle form submission
    const handleSubmit = async () => {
        const isValid = validateForm();

        if (!isValid) {
            return;
        }

        try {
            if (isEditMode) {
                // For edit mode, only send the updatable fields (UpdateTenant)
                const updateData: UpdateTenant = {
                    name: formData.name,
                };
                await onSubmit(updateData);
            } else {
                // For create mode, send the full form data (CreateTenant)
                await onSubmit(formData);
            }
        } catch (error) {
            setErrors((prev) => ({
                ...prev,
                general: error instanceof Error ? error.message : 'An unexpected error occurred',
            }));
        }
    };

    const handleCancel = () => {
        setFormData({
            name: '',
            subDomain: '',
            region: '',
            initialUser: { firstName: '', lastName: '', email: '' },
        });
        setErrors({});
        setSubdomainValidationMessage('');
        onCancel();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="md"
            fullWidth
            slotProps={{
                paper: {
                    sx: { minHeight: '600px' },
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h5" component="h2">
                    {isEditMode ? 'Edit Tenant' : 'Create New Tenant'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isEditMode ? 'Update tenant information and configuration' : 'Create a new tenant with initial administrator user'}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* General Error */}
                    {errors.general && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.general}
                        </Alert>
                    )}

                    {/* Tenant Information Section */}
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Tenant Information
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Tenant Name"
                                value={formData.name}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                error={Boolean(errors.name)}
                                helperText={errors.name || 'A descriptive name for the tenant organization'}
                                fullWidth
                                required
                                disabled={loading}
                            />

                            <TextField
                                label="Subdomain"
                                value={formData.subDomain}
                                onChange={(e) => handleFieldChange('subDomain', e.target.value.toLowerCase())}
                                error={Boolean(errors.subDomain)}
                                helperText={
                                    errors.subDomain ||
                                    subdomainValidationMessage ||
                                    'Unique subdomain for tenant access (lowercase, alphanumeric, hyphens allowed)'
                                }
                                fullWidth
                                required
                                disabled={loading || isEditMode} // Subdomain cannot be changed in edit mode
                                slotProps={{
                                    input: {
                                        endAdornment: subdomainValidating && <CircularProgress size={20} />,
                                    },
                                }}
                            />

                            <FormControl fullWidth required error={Boolean(errors.region)} disabled={loading || isEditMode}>
                                <InputLabel id="region-select-label">AWS Region</InputLabel>
                                <Select
                                    labelId="region-select-label"
                                    value={formData.region}
                                    label="AWS Region"
                                    onChange={(e) => handleFieldChange('region', e.target.value)}
                                >
                                    {AWS_REGIONS.map((region) => (
                                        <MenuItem key={region.value} value={region.value}>
                                            {region.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {errors.region ||
                                        (isEditMode ? 'Region cannot be changed after creation' : 'AWS region where tenant resources will be deployed')}
                                </FormHelperText>
                            </FormControl>
                        </Box>
                    </Box>

                    {/* Initial User Section - Only show in create mode */}
                    {!isEditMode && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Initial Administrator User
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    This user will be created as the first administrator for the tenant
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField
                                            label="First Name"
                                            value={formData.initialUser.firstName}
                                            onChange={(e) => handleInitialUserChange('firstName', e.target.value)}
                                            error={Boolean(errors.initialUser?.firstName)}
                                            helperText={errors.initialUser?.firstName}
                                            fullWidth
                                            required
                                            disabled={loading}
                                        />
                                        <TextField
                                            label="Last Name"
                                            value={formData.initialUser.lastName}
                                            onChange={(e) => handleInitialUserChange('lastName', e.target.value)}
                                            error={Boolean(errors.initialUser?.lastName)}
                                            helperText={errors.initialUser?.lastName}
                                            fullWidth
                                            required
                                            disabled={loading}
                                        />
                                    </Box>

                                    <TextField
                                        label="Email Address"
                                        type="email"
                                        value={formData.initialUser.email}
                                        onChange={(e) => handleInitialUserChange('email', e.target.value)}
                                        error={Boolean(errors.initialUser?.email)}
                                        helperText={errors.initialUser?.email || 'Email address for the initial administrator user'}
                                        fullWidth
                                        required
                                        disabled={loading}
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={handleCancel} disabled={loading} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading || subdomainValidating || (Boolean(errors.subDomain) && !isEditMode)}
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : undefined}
                >
                    {loading ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update Tenant' : 'Create Tenant'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Utility function for debouncing
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
    let timeout: NodeJS.Timeout;
    return ((...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    }) as T;
}
